import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { Loader2, X } from "lucide-react";

/**
 * Irse de "Varios productos a la vez" con productos cargados y sin crear: crearlos,
 * descartarlos o quedarse — ADR-2026-10-14-varios-pregunta-antes-de-salir. La misma
 * pregunta que el editor de posts (`PreguntaDeSalida` de `EditorDePost`), con los textos de
 * la carga. La X, Escape y tocar afuera son "seguir editando": cerrar la pregunta no decide
 * nada.
 *
 * Va por portal y por encima de todo (`z-[230]`): la usan la pantalla de Mi marca y el
 * pop-up de subir de los modos (`ModalDeSubirProductos`, `z-[210]`).
 */
export const PreguntaDeSalidaDeCarga: React.FC<{
  /** Cuántos productos se crearían ("Crear 4 productos"). */
  etiquetaDeCrear: string;
  puedeCrear: boolean;
  creando: boolean;
  onCrear: () => void;
  onDescartar: () => void;
  onQuedarse: () => void;
}> = ({ etiquetaDeCrear, puedeCrear, creando, onCrear, onDescartar, onQuedarse }) => {
  useEffect(() => {
    const alTeclado = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !creando) onQuedarse();
    };
    window.addEventListener("keydown", alTeclado);
    return () => window.removeEventListener("keydown", alTeclado);
  }, [onQuedarse, creando]);

  return createPortal(
    <div
      className="fixed inset-0 z-[230] flex items-end justify-center bg-black/40 motion-safe:animate-[aparecer-suave_0.2s_ease-out_both] sm:items-center sm:p-4"
      // `stopPropagation`: va por portal, pero en React el clic sigue subiendo por quien la
      // monta — en el pop-up de los modos llegaba a su fondo, que la volvía a abrir.
      onClick={(e) => {
        e.stopPropagation();
        if (!creando) onQuedarse();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-salida-carga"
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-t-3xl bg-white p-6 shadow-xl sm:rounded-3xl"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}
      >
        <button
          type="button"
          onClick={onQuedarse}
          disabled={creando}
          aria-label="Seguir editando"
          title="Seguir editando"
          className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full text-black/40 transition-colors hover:bg-black/[0.05] hover:text-[#16181D] disabled:opacity-40"
        >
          <X className="h-4 w-4" />
        </button>
        <h2 id="titulo-salida-carga" className="pr-8 text-[18px] font-semibold text-[#16181D]">
          ¿Creás los productos antes de salir?
        </h2>
        <p className="mt-1.5 text-[14px] leading-snug text-black/55">
          Todavía no están en tu catálogo. Si los descartás, se pierden las fotos que cargaste.
        </p>
        <div className="mt-6 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={onDescartar}
            disabled={creando}
            className="inline-flex h-11 items-center justify-center rounded-full border border-black/[0.08] bg-white px-4 text-[14px] font-semibold text-error transition-colors hover:border-[#F5C2C0] hover:bg-error-soft disabled:opacity-50"
          >
            Descartar
          </button>
          <button
            type="button"
            onClick={onCrear}
            disabled={!puedeCrear || creando}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-full bg-brand-primary px-4 text-[14px] font-semibold text-white shadow-[0_6px_16px_-6px_rgb(var(--brand-primary-rgb)_/_0.55)] transition-colors hover:bg-brand-primary-hover disabled:cursor-not-allowed disabled:opacity-60"
          >
            {creando && <Loader2 className="h-4 w-4 animate-spin" />}
            {creando ? "Creando…" : etiquetaDeCrear}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
